import React, { useState, useEffect } from 'react';
import { Card, Button, Progress, Spin, message, Select } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer
} from 'recharts';
import { statsAPI } from '@/services/api/stats';
import { deviceModelsAPI } from '@/services/api/device-models';
import type { VersionDistributionItem, DeviceModel } from '@/types/api';

interface VersionData {
  version: string;
  count: number;
  percentage: number;
}

const VersionStatistics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [versionData, setVersionData] = useState<VersionData[]>([]);
  const [detailedData, setDetailedData] = useState<VersionData[]>([]);
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<number | undefined>(undefined);
  const [modelsLoaded, setModelsLoaded] = useState(false);

  // 获取机型列表
  useEffect(() => {
    const fetchDeviceModels = async () => {
      try {
        const response = await deviceModelsAPI.getDeviceModels({
          page: 1,
          size: 100,
          status: 'in_production', // 只获取生产中的机型
        });
        if (response.list && response.list.length > 0) {
          setDeviceModels(response.list);
          // 默认选择第一个机型
          if (response.list.length > 0) {
            setSelectedModelId(response.list[0].id);
          }
          setModelsLoaded(true);
        } else {
          setModelsLoaded(true);
        }
      } catch (error) {
        console.error('获取机型列表失败:', error);
        message.error('获取机型列表失败');
        setModelsLoaded(true);
      }
    };
    fetchDeviceModels();
  }, []);

  // 获取版本统计数据
  const fetchVersionStatistics = async () => {
    // 如果机型列表还没加载完成，不执行数据获取
    if (!modelsLoaded) {
      return;
    }
    try {
      setLoading(true);
      // 构建请求参数
      const params = selectedModelId ? { model_id: selectedModelId } : undefined;
      // 并行获取版本分布和详细分布数据
      const [distribution, detailDistribution] = await Promise.all([
        statsAPI.getVersionDistribution(params),
        statsAPI.getVersionDetailDistribution(params),
      ]);

      // 转换版本分布数据
      const versionDataList: VersionData[] = distribution.items.map((item: VersionDistributionItem) => ({
        version: item.version,
        count: item.count,
        percentage: Math.round(item.percent * 100) / 100, // 保留两位小数（后端返回的percent已经是0-100的百分比）
      }));
      setVersionData(versionDataList);

      // 转换版本详细分布数据
      const detailedDataList: VersionData[] = detailDistribution.items.map((item: VersionDistributionItem) => ({
        version: item.version,
        count: item.count,
        percentage: Math.round(item.percent * 100) / 100, // 保留两位小数（后端返回的percent已经是0-100的百分比）
      }));
      setDetailedData(detailedDataList);
    } catch (error: any) {
      console.error('获取版本统计数据失败:', error);
      message.error(error?.message || '获取版本统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersionStatistics();
  }, [selectedModelId, modelsLoaded]);

  // 自定义Tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-3 border rounded-lg shadow-lg" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
          <p className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // 处理导出设备列表
  const handleExportDevices = async (version: string) => {
    try {
      message.loading({ content: '正在导出设备列表...', key: 'export' });
      const params: { version: string; model_id?: number } = { version };
      if (selectedModelId) {
        params.model_id = selectedModelId;
      }
      const result = await statsAPI.exportDevicesByVersion(params);
      message.success({ content: '导出成功', key: 'export' });
      // 打开下载链接
      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch (error: any) {
      console.error('导出设备列表失败:', error);
      message.error({ content: error?.message || '导出设备列表失败', key: 'export' });
    }
  };

  // 计算Y轴最大值（用于图表显示）
  const maxCount = versionData.length > 0 
    ? Math.max(...versionData.map(item => item.count))
    : 0;
  const yAxisMax = maxCount > 0 ? Math.ceil(maxCount * 1.1) : 1000;

  return (
    <div className="space-y-6">
      {/* 页面标题和机型选择 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>版本统计</h1>
          <p style={{ color: 'var(--text-secondary)' }}>查看各版本设备分布和详细数据统计</p>
        </div>
        <div className="flex items-center space-x-4">
          <span style={{ color: 'var(--text-secondary)' }}>机型:</span>
          <Select
            value={selectedModelId}
            onChange={setSelectedModelId}
            placeholder="全部机型"
            allowClear
            style={{ width: 200 }}
            loading={loading}
          >
            {deviceModels.map(model => (
              <Select.Option key={model.id} value={model.id}>
                {model.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center" style={{ minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* 版本分布图表 */}
          <Card 
        title="版本分布" 
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
        styles={{
          header: {
            background: 'var(--bg-tertiary)',
            borderBottom: '1px solid var(--border-primary)',
            color: 'var(--text-primary)'
          }
        }}
      >
        <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>当前各版本的设备数量</p>
        
        {versionData.length > 0 ? (
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={versionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" />
              <XAxis 
                dataKey="version" 
                stroke="var(--text-secondary)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--text-secondary)"
                fontSize={12}
                domain={[0, yAxisMax]}
              />
              <Tooltip 
                content={<CustomTooltip />}
                contentStyle={{
                  backgroundColor: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '6px',
                  color: 'var(--text-primary)'
                }}
              />
              <Legend />
              <Bar 
                dataKey="count" 
                fill="#1890ff"
                name="设备数"
              />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex justify-center items-center" style={{ height: '300px', color: 'var(--text-secondary)' }}>
            暂无数据
          </div>
        )}
      </Card>

      {/* 版本详细数据 */}
      <Card 
        title="版本详细数据" 
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
        styles={{
          header: {
            background: 'var(--bg-tertiary)',
            borderBottom: '1px solid var(--border-primary)',
            color: 'var(--text-primary)'
          }
        }}
      >
        <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>各版本设备数和占比详情，可导出设备列表</p>
        
        {detailedData.length > 0 ? (
          <div className="space-y-4">
            {detailedData.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-4 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
              {/* 排名 */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-4" style={{ background: 'var(--color-primary)', color: 'var(--text-inverse)' }}>
                {index + 1}
              </div>

              {/* 版本号（加宽以完整显示） */}
              <div className="flex-shrink-0 w-32">
                <span className="font-medium" style={{ color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                  {item.version}
                </span>
              </div>

              {/* 进度条（缩短一点） */}
              <div className="mx-4" style={{ flex: 1, maxWidth: '45%' }}>
                <Progress 
                  percent={item.percentage} 
                  strokeColor="var(--color-primary)"
                  trailColor="var(--border-secondary)"
                  showInfo={false}
                  size="small"
                />
              </div>
              
              {/* 设备数量 */}
              <div className="flex-shrink-0 w-24 text-right">
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{item.count.toLocaleString()} 台</span>
              </div>
              
              {/* 百分比 */}
              <div className="flex-shrink-0 w-16 text-right">
                <span className="font-medium" style={{ color: 'var(--color-primary)' }}>{item.percentage}%</span>
              </div>
              
              {/* 导出按钮 */}
              <div className="flex-shrink-0 ml-4">
                <Button
                  type="primary"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => handleExportDevices(item.version)}
                >
                  导出设备
                </Button>
              </div>
            </div>
            ))}
          </div>
        ) : (
          <div className="flex justify-center items-center" style={{ minHeight: '200px', color: 'var(--text-secondary)' }}>
            暂无数据
          </div>
        )}
      </Card>
        </>
      )}
    </div>
  );
};

export default VersionStatistics;
